import { InstagramRequirements } from "@/components/channel/instagram-requirements";
import { PLATFORM_CONFIGS } from "@/components/channel/platform-utils";
import {
  AD_ACCESS_PLATFORMS,
  AdAccessOnboardingIntro,
  AdAccessPlatformFields,
  adAccessItemsFromState,
  adAccessStateFromPreferences,
  isAdAccessStateComplete,
  validateAdAccessEntry,
  type AdAccessEntry,
  type AdAccessPlatform,
  type AdAccessState,
} from "@/components/settings/ad-access-rates-select";
import { CAMPAIGN_TYPE_OPTIONS } from "@/lib/constants/campaign-types";
import { CONTENT_CATEGORY_OPTIONS } from "@/lib/constants/content-categories";
import type { ChannelType, Platform } from "@/lib/queries/channel";
import {
  OAUTH_RETURN_URL_KEY,
  channelListQueryOptions,
  useConnectChannel,
  useDisconnectChannel,
} from "@/lib/queries/channel";
import { getPlatformLabel } from "@/components/channel/platform-utils";
import { getCountries, getRegions } from "@/lib/data/country-data";
import { useUpdateOrganization } from "@/lib/queries/organization";
import { ensureOrgContext } from "@/lib/utils/org-context-guard";
import {
  profileQueryOptions,
  useEnsureProfile,
  useProfileListQuery,
  useUpdateProfile,
} from "@/lib/queries/profile";
import {
  profilePreferencesQueryOptions,
  useSetProfilePreferences,
} from "@/lib/queries/profile-preferences";
import {
  isOnboardingCompleted,
  getOnboardingStep,
  getOrganizationType,
  parseOrganizationMetadata,
  type OrganizationMetadata,
} from "@/lib/utils/organization";
import type { OrgRouteContext } from "./route";
import {
  Alert,
  AlertDescription,
  Button,
  Card,
  CardContent,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Input,
  Label,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Switch,
  ToggleGroup,
  ToggleGroupItem,
} from "@repo/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Check,
  ChevronsUpDown,
  ExternalLink,
  Loader2,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { captureEvent } from "@/lib/analytics-loader";

const countries = getCountries();

const MAX_CONTENT_CATEGORIES = 3;

/**
 * Build a merged metadata object from the organization's current metadata
 * string plus new fields. Uses lastSavedStepRef to track what step was
 * last persisted, guarding against stale route context between rapid saves.
 */
function mergeOrgMetadata(
  currentMetadata: string | null | undefined,
  lastSavedStep: number,
  updates: Partial<OrganizationMetadata>,
): OrganizationMetadata {
  const existing = parseOrganizationMetadata(currentMetadata);
  // Gather all step sources: existing metadata, ref-tracked last save, and the incoming update
  const existingStep =
    typeof existing.onboardingStep === "number" ? existing.onboardingStep : 0;
  const updateStep =
    typeof updates.onboardingStep === "number" ? updates.onboardingStep : 0;
  // Never regress — take the highest step across all sources, clamped to valid
  // range. Sources are non-negative integers, so the clamp stays in 0-5.
  const rawStep = Math.max(existingStep, lastSavedStep, updateStep);
  const effectiveStep = Math.min(rawStep, 5) as 0 | 1 | 2 | 3 | 4 | 5;
  return { ...existing, ...updates, onboardingStep: effectiveStep };
}

export const Route = createFileRoute("/(org)/o/$orgSlug/onboarding")({
  beforeLoad: ({ context, params }) => {
    const { organization } = context as OrgRouteContext;
    if (
      !ensureOrgContext(
        organization,
        "(org)/o.$orgSlug/onboarding.tsx#beforeLoad",
        { orgSlug: params.orgSlug },
      )
    ) {
      throw redirect({ to: "/", search: { error: "organization_not_found" } });
    }
    const orgType = getOrganizationType(organization.metadata);

    // Agencies skip onboarding entirely
    if (orgType === "agency") {
      throw redirect({
        to: "/o/$orgSlug/dashboard",
        params: { orgSlug: params.orgSlug },
      });
    }

    // Already onboarded — go to dashboard
    if (isOnboardingCompleted(organization.metadata)) {
      throw redirect({
        to: "/o/$orgSlug/dashboard",
        params: { orgSlug: params.orgSlug },
      });
    }
  },
  component: OnboardingPage,
});

function OnboardingPage() {
  const { organization, role } = Route.useRouteContext() as OrgRouteContext;
  const { orgSlug } = Route.useParams();
  const navigate = useNavigate();

  // Mutations
  const updateOrg = useUpdateOrganization();
  const updateProfile = useUpdateProfile(organization.id);
  const connectChannel = useConnectChannel();
  const disconnectChannel = useDisconnectChannel(organization.id);
  const profileList = useProfileListQuery(organization.id);
  const profileId = profileList.data?.[0]?.id ?? "";
  const isProfileReady = Boolean(profileId);

  // Auto-create profile if the list loaded successfully but is empty.
  // Only for the org owner — admins viewing this page should not create
  // a profile for themselves in another user's organization.
  const isOwner = role === "owner";
  const ensureProfile = useEnsureProfile(organization.id);
  const { mutate: ensureProfileMutate, isPending: ensureProfilePending } =
    ensureProfile;
  const ensureProfileTriggeredRef = useRef(false);
  useEffect(() => {
    if (
      isOwner &&
      profileList.isSuccess &&
      profileList.data.length === 0 &&
      !ensureProfileTriggeredRef.current &&
      !ensureProfilePending
    ) {
      ensureProfileTriggeredRef.current = true;
      ensureProfileMutate();
    }
  }, [
    isOwner,
    profileList.isSuccess,
    profileList.data,
    ensureProfileMutate,
    ensureProfilePending,
  ]);

  // Derive a user-facing error when profile setup fails or isn't possible
  const profileSetupError =
    !isProfileReady && profileList.isSuccess && profileList.data.length === 0
      ? ensureProfile.isError
        ? "Something went wrong setting up your profile. Please try refreshing the page or contact support."
        : !isOwner
          ? "Your account doesn't have permission to complete onboarding for this organization. Please contact the organization owner."
          : null // still loading / mutation in-flight
      : null;

  const setPreferences = useSetProfilePreferences(organization.id, profileId);
  const channelList = useQuery({
    ...channelListQueryOptions(organization.id, profileId),
    enabled: isProfileReady,
  });

  // Fetch profile detail (includes addresses) for resume pre-fill and address upsert
  const profileDetail = useQuery({
    ...profileQueryOptions(organization.id, profileId),
    enabled: isProfileReady,
  });
  // Fetch saved preferences for resume pre-fill (steps 2-4)
  const preferencesQuery = useQuery({
    ...profilePreferencesQueryOptions(organization.id, profileId),
    enabled: isProfileReady,
  });
  // Resume at the first incomplete step based on saved progress
  const completedStep = getOnboardingStep(organization.metadata);
  const initialStep = Math.min(completedStep + 1, 5) as 1 | 2 | 3 | 4 | 5;

  // Wizard state
  const [step, setStep] = useState<1 | 2 | 3 | 4 | 5>(initialStep);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const lastSavedStepRef = useRef(completedStep);

  // Track which step events have already been captured to prevent duplicates
  // when the user navigates back/forward. Pre-populated with steps completed
  // in a previous session so resuming doesn't re-fire.
  const capturedStepsRef = useRef(
    new Set<number>(Array.from({ length: completedStep }, (_, i) => i + 1)),
  );

  // Fire a deduped analytics event for a completed onboarding step
  function captureStepOnce(
    stepId: number,
    properties: Record<string, string | number | boolean | string[]>,
  ) {
    if (capturedStepsRef.current.has(stepId)) return;
    capturedStepsRef.current.add(stepId);
    void captureEvent("onboarding_step_completed", {
      step: stepId,
      ...properties,
    });
  }

  // Step 1: Creator basics — store only user edits, fall back to profile data.
  // This avoids useEffect + setState for pre-filling on resume.
  interface Step1Edits {
    firstName?: string;
    lastName?: string;
    city?: string;
    state?: string;
    country?: string;
  }
  const [step1Edits, setStep1Edits] = useState<Step1Edits>({});
  const profileData = profileDetail.data;
  const firstName = step1Edits.firstName ?? profileData?.firstName ?? "";
  const lastName = step1Edits.lastName ?? profileData?.lastName ?? "";
  const city = step1Edits.city ?? profileData?.city ?? "";
  const state = step1Edits.state ?? profileData?.state ?? "";
  const country = step1Edits.country ?? profileData?.country ?? "";

  const setFirstName = (v: string) =>
    setStep1Edits((prev) => ({ ...prev, firstName: v }));
  const setLastName = (v: string) =>
    setStep1Edits((prev) => ({ ...prev, lastName: v }));
  const setCity = (v: string) =>
    setStep1Edits((prev) => ({ ...prev, city: v }));
  const setState = (v: string) =>
    setStep1Edits((prev) => ({ ...prev, state: v }));
  const setCountry = (v: string) =>
    setStep1Edits((prev) => ({ ...prev, country: v }));

  // Step 2: Campaign interests — nullable state overlays server data
  const [campaignTypesEdits, setCampaignTypesEdits] = useState<string[] | null>(
    null,
  );
  const [campaignOtherTextEdit, setCampaignOtherTextEdit] = useState<
    string | null
  >(null);
  const savedCampaignTypes = useMemo(
    () =>
      (preferencesQuery.data?.grouped["campaign_types"] ?? []).map(
        (p) => p.key,
      ),
    [preferencesQuery.data],
  );
  const savedCampaignOtherText = useMemo(() => {
    const other = (preferencesQuery.data?.grouped["campaign_types"] ?? []).find(
      (p) => p.key === "Other",
    );
    return other?.textValue ?? "";
  }, [preferencesQuery.data]);
  const campaignTypes = campaignTypesEdits ?? savedCampaignTypes;
  const campaignOtherText = campaignOtherTextEdit ?? savedCampaignOtherText;
  const setCampaignTypes = (v: string[]) => setCampaignTypesEdits(v);
  const setCampaignOtherText = (v: string) => setCampaignOtherTextEdit(v);

  // Step 3: Content categories — nullable state overlays server data
  const [contentCategoriesEdits, setContentCategoriesEdits] = useState<
    string[] | null
  >(null);
  const savedContentCategories = useMemo(
    () =>
      (preferencesQuery.data?.grouped["content_categories"] ?? []).map(
        (p) => p.key,
      ),
    [preferencesQuery.data],
  );
  const contentCategories = contentCategoriesEdits ?? savedContentCategories;
  const setContentCategories = (v: string[]) => setContentCategoriesEdits(v);

  // Step 4: Ad access rates — nullable state overlays server data.
  // Required step (EPD-10998): Continue is gated on isAdAccessStateComplete.
  const [adAccessEdits, setAdAccessEdits] = useState<AdAccessState | null>(
    null,
  );
  const savedAdAccess = useMemo(
    () =>
      adAccessStateFromPreferences(
        preferencesQuery.data?.grouped["ad_access_rates"] ?? [],
      ),
    [preferencesQuery.data],
  );
  const adAccess = adAccessEdits ?? savedAdAccess;
  const handleAdAccessUpdate = (
    platform: AdAccessPlatform,
    field: keyof AdAccessEntry,
    value: string,
  ) => {
    setAdAccessEdits({
      ...adAccess,
      [platform]: { ...adAccess[platform], [field]: value },
    });
  };

  // Validation
  const step1Valid =
    firstName.trim() !== "" &&
    lastName.trim() !== "" &&
    city.trim() !== "" &&
    state.trim() !== "" &&
    country.trim() !== "";
  const step2Valid = campaignTypes.length > 0;
  const step3Valid = contentCategories.length > 0;
  // EPD-10998: every platform answered; "Yes" answers need a rate + negotiability
  const step4Valid = isAdAccessStateComplete(adAccess);

  function handleContentCategoryChange(values: string[]) {
    if (values.length <= MAX_CONTENT_CATEGORIES) {
      setContentCategories(values);
    }
  }

  // Step 1: Profile basics + location
  async function handleStep1Continue() {
    if (!profileId) return;
    setIsSubmitting(true);

    try {
      await updateProfile.mutateAsync({
        profileId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        city: city.trim(),
        state: state.trim(),
        country: country.trim(),
      });

      const metadata = mergeOrgMetadata(
        organization.metadata,
        lastSavedStepRef.current,
        { onboardingStep: 1 },
      );
      await updateOrg.mutateAsync({
        organizationId: organization.id,
        metadata,
      });
      lastSavedStepRef.current = 1;

      captureStepOnce(1, { step_name: "basics", country: country.trim() });

      setIsSubmitting(false);
      setStep(2);
    } catch (err) {
      console.error("Onboarding step 1 save failed", err);
      toast.error("Failed to save your profile. Please try again.");
      setIsSubmitting(false);
    }
  }

  // Step 2: Campaign interests
  async function handleStep2Continue() {
    if (!profileId) return;
    setIsSubmitting(true);

    try {
      const campaignItems = campaignTypes.map((key, i) => ({
        key,
        textValue: key === "Other" ? campaignOtherText || null : null,
        sortOrder: i,
      }));

      await setPreferences.mutateAsync({
        category: "campaign_types",
        items: campaignItems,
      });

      const metadata = mergeOrgMetadata(
        organization.metadata,
        lastSavedStepRef.current,
        { onboardingStep: 2 },
      );
      await updateOrg.mutateAsync({
        organizationId: organization.id,
        metadata,
      });
      lastSavedStepRef.current = 2;

      captureStepOnce(2, {
        step_name: "campaign_interests",
        campaign_types: campaignTypes,
      });

      setIsSubmitting(false);
      setStep(3);
    } catch (err) {
      console.error("Onboarding step 2 save failed", err);
      toast.error("Failed to save your preferences. Please try again.");
      setIsSubmitting(false);
    }
  }

  // Step 3: Content categories
  async function handleStep3Continue() {
    if (!profileId) return;
    setIsSubmitting(true);

    try {
      const contentItems = contentCategories.map((key, i) => ({
        key,
        sortOrder: i,
      }));

      await setPreferences.mutateAsync({
        category: "content_categories",
        items: contentItems,
      });

      const metadata = mergeOrgMetadata(
        organization.metadata,
        lastSavedStepRef.current,
        { onboardingStep: 3 },
      );
      await updateOrg.mutateAsync({
        organizationId: organization.id,
        metadata,
      });
      lastSavedStepRef.current = 3;

      captureStepOnce(3, {
        step_name: "content_categories",
        content_categories: contentCategories,
      });

      setIsSubmitting(false);
      setStep(4);
    } catch (err) {
      console.error("Onboarding step 3 save failed", err);
      toast.error("Failed to save your content categories. Please try again.");
      setIsSubmitting(false);
    }
  }

  // Step 4: Ad access rates (optional)
  async function handleStep4Continue() {
    if (!profileId) return;

    // Validate answered entries before saving
    for (const platform of AD_ACCESS_PLATFORMS) {
      const error = validateAdAccessEntry(platform, adAccess[platform]);
      if (error) {
        toast.error(error);
        return;
      }
    }

    setIsSubmitting(true);

    try {
      // Only persist when the user actually made edits — a creator resuming
      // with complete saved answers can continue without a redundant rewrite.
      if (adAccessEdits !== null) {
        await setPreferences.mutateAsync({
          category: "ad_access_rates",
          items: adAccessItemsFromState(adAccess),
        });
      }

      const metadata = mergeOrgMetadata(
        organization.metadata,
        lastSavedStepRef.current,
        { onboardingStep: 4 },
      );
      await updateOrg.mutateAsync({
        organizationId: organization.id,
        metadata,
      });
      lastSavedStepRef.current = 4;

      captureStepOnce(4, {
        step_name: "ad_access_rates",
        answered_platforms: AD_ACCESS_PLATFORMS.filter(
          (platform) => adAccess[platform].access !== "",
        ),
      });

      setIsSubmitting(false);
      setStep(5);
    } catch (err) {
      console.error("Onboarding step 4 save failed", err);
      toast.error("Failed to save your ad access rates. Please try again.");
      setIsSubmitting(false);
    }
  }

  // Step 5: Connect socials (final step)
  async function handleSubmit() {
    setIsSubmitting(true);

    try {
      const metadata = mergeOrgMetadata(
        organization.metadata,
        lastSavedStepRef.current,
        { onboardingStep: 5, onboardingCompleted: true },
      );
      await updateOrg.mutateAsync({
        organizationId: organization.id,
        metadata,
      });

      const platforms = (channelList.data ?? []).map((c) => c.platform);
      const isFirstCompletion = !capturedStepsRef.current.has(5);
      captureStepOnce(5, {
        step_name: "connect_socials",
        connected_platforms: platforms,
        connected_count: platforms.length,
      });
      if (isFirstCompletion) {
        void captureEvent("onboarding_completed", {
          total_steps: 5,
          connected_platforms: platforms,
          connected_count: platforms.length,
        });
      }

      navigate({ to: "/o/$orgSlug/dashboard", params: { orgSlug } });
    } catch (err) {
      console.error("Onboarding submit failed", err);
      toast.error("Failed to complete onboarding. Please try again.");
      setIsSubmitting(false);
    }
  }

  const handleDisconnectChannel = async (channelId: string) => {
    try {
      await disconnectChannel.mutateAsync({
        organizationId: organization.id,
        channelId,
      });
    } catch (error) {
      console.error("Failed to disconnect:", error);
      toast.error("Failed to remove account. Please try again.");
    }
  };

  const handleConnectPlatform = async (
    platform: Platform,
    channelType: ChannelType,
  ) => {
    if (!profileId) return;
    try {
      const result = await connectChannel.mutateAsync({
        organizationId: organization.id,
        profileId,
        platform,
        channelType,
      });
      if (result && typeof result === "object" && "url" in result) {
        sessionStorage.setItem(OAUTH_RETURN_URL_KEY, window.location.pathname);
        window.location.href = result.url as string;
      }
    } catch (error) {
      console.error("Failed to connect:", error);
      toast.error("Failed to start connection. Please try again.");
    }
  };

  return (
    <div className="flex flex-1 flex-col h-full">
      {/* Scrollable content area */}
      <div className="flex flex-1 items-center justify-center overflow-y-auto p-6 pb-24">
        <div className="flex flex-col items-center gap-8 w-full max-w-xl">
          {/* Header */}
          <div className="flex flex-col items-center gap-2 text-center">
            <h1 className="text-2xl font-agrandir-medium tracking-tight text-primary">
              {step === 1 && "Let's Get to Know You"}
              {step === 2 && "What Are You Looking For?"}
              {step === 3 && "What's Your Content Niche?"}
              {step === 4 && "Ad Access Rates"}
              {step === 5 && "Connect Your Socials"}
            </h1>
            {step === 5 && (
              <p className="text-sm text-muted-foreground">(Optional)</p>
            )}
          </div>

          {/* Profile setup error */}
          {profileSetupError && (
            <Alert variant="destructive" className="w-full">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{profileSetupError}</AlertDescription>
            </Alert>
          )}

          {/* Step content */}
          <div className="w-full space-y-6">
            {step === 1 && (
              <StepBasics
                firstName={firstName}
                lastName={lastName}
                city={city}
                state={state}
                country={country}
                onFirstNameChange={setFirstName}
                onLastNameChange={setLastName}
                onCityChange={setCity}
                onStateChange={setState}
                onCountryChange={setCountry}
              />
            )}

            {step === 2 && (
              <StepCampaignInterests
                campaignTypes={campaignTypes}
                campaignOtherText={campaignOtherText}
                onCampaignTypesChange={setCampaignTypes}
                onCampaignOtherTextChange={setCampaignOtherText}
              />
            )}

            {step === 3 && (
              <StepContentCategories
                contentCategories={contentCategories}
                onContentCategoriesChange={handleContentCategoryChange}
              />
            )}

            {step === 4 && (
              <StepAdAccessRates
                entries={adAccess}
                onUpdate={handleAdAccessUpdate}
              />
            )}

            {step === 5 && (
              <StepConnectSocials
                onConnect={handleConnectPlatform}
                onDisconnect={handleDisconnectChannel}
                isConnecting={connectChannel.isPending}
                isDisconnecting={disconnectChannel.isPending}
                isProfileReady={isProfileReady}
                connectedChannels={channelList.data ?? []}
              />
            )}
          </div>
        </div>
      </div>

      {/* Fixed footer */}
      <div className="sticky bottom-0 bg-background px-6 py-4">
        {step === 4 && (
          <p className="mx-auto mb-3 max-w-xl text-center text-xs text-muted-foreground">
            You can update these anytime from your profile settings.
          </p>
        )}
        <div className="mx-auto flex max-w-xl gap-3">
          {step > 1 && (
            <Button
              variant="outline"
              onClick={() => setStep((s) => (s - 1) as 1 | 2 | 3 | 4 | 5)}
              disabled={isSubmitting}
              className="flex-1"
            >
              <ArrowLeft className="size-4 mr-2" />
              Back
            </Button>
          )}

          {step < 5 && (
            <Button
              onClick={
                step === 1
                  ? handleStep1Continue
                  : step === 2
                    ? handleStep2Continue
                    : step === 3
                      ? handleStep3Continue
                      : handleStep4Continue
              }
              disabled={
                !isProfileReady ||
                isSubmitting ||
                (step === 1
                  ? !step1Valid
                  : step === 2
                    ? !step2Valid
                    : step === 3
                      ? !step3Valid
                      : !step4Valid)
              }
              className="flex-1"
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="size-4 mr-2" />
              )}
              Continue
            </Button>
          )}

          {step === 5 && (
            <Button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <Loader2 className="size-4 animate-spin mr-2" />
              ) : (
                <ArrowRight className="size-4 mr-2" />
              )}
              Take me to the creator portal!
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Ad Access Rates
// ---------------------------------------------------------------------------

function StepAdAccessRates({
  entries,
  onUpdate,
}: {
  entries: AdAccessState;
  onUpdate: (
    platform: AdAccessPlatform,
    field: keyof AdAccessEntry,
    value: string,
  ) => void;
}) {
  return (
    <div className="space-y-4">
      <AdAccessOnboardingIntro />
      {AD_ACCESS_PLATFORMS.map((platform) => (
        <Card key={platform}>
          <CardContent className="p-4 space-y-4">
            <AdAccessPlatformFields
              platform={platform}
              entry={entries[platform]}
              onUpdate={onUpdate}
              idPrefix="onboarding-ad-access-rate"
              showHelp
            />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Connect Socials
// ---------------------------------------------------------------------------

interface ConnectedChannel {
  id: string;
  platform: Platform;
  handle?: string | null;
  displayName?: string | null;
}

function StepConnectSocials({
  onConnect,
  onDisconnect,
  isConnecting,
  isDisconnecting,
  isProfileReady,
  connectedChannels,
}: {
  onConnect: (platform: Platform, channelType: ChannelType) => void;
  onDisconnect: (channelId: string) => void;
  isConnecting: boolean;
  isDisconnecting: boolean;
  isProfileReady: boolean;
  connectedChannels: ConnectedChannel[];
}) {
  const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(
    null,
  );
  const [isBusinessAccount, setIsBusinessAccount] = useState(false);

  const selectedConfig = selectedPlatform
    ? PLATFORM_CONFIGS.find((p) => p.platform === selectedPlatform)
    : null;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground text-center">
        Connect your social accounts to access more campaign opportunities. You
        can skip this step and connect them anytime from your settings.
      </p>

      <Card>
        <CardContent className="p-4 space-y-4">
          {selectedPlatform && selectedConfig ? (
            // Confirmation step — matches ChannelConnectDialog pattern
            <div className="space-y-4">
              <div className="flex items-center gap-4 p-4 rounded-lg border">
                <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                  <selectedConfig.icon className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <p className="font-medium">{selectedConfig.label}</p>
                  <p className="text-sm text-muted-foreground">
                    {selectedConfig.description}
                  </p>
                </div>
              </div>

              {selectedPlatform === "instagram" && <InstagramRequirements />}

              {selectedConfig.supportsBusiness && (
                <div className="flex items-center justify-between p-4 rounded-lg border">
                  <div className="space-y-0.5">
                    <Label htmlFor="business-mode">Business Account</Label>
                    <p className="text-sm text-muted-foreground">
                      Connect as a business account for ads and analytics
                    </p>
                  </div>
                  <Switch
                    id="business-mode"
                    checked={isBusinessAccount}
                    onCheckedChange={setIsBusinessAccount}
                  />
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setSelectedPlatform(null);
                    setIsBusinessAccount(false);
                  }}
                >
                  Back
                </Button>
                <Button
                  className="flex-1"
                  disabled={isConnecting}
                  onClick={() => {
                    const channelType: ChannelType = isBusinessAccount
                      ? "business"
                      : "creator";
                    onConnect(selectedPlatform, channelType);
                  }}
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Connect
                    </>
                  )}
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Connect buttons — always visible for all platforms */}
              <div className="grid grid-cols-2 gap-3">
                {PLATFORM_CONFIGS.map((config) => {
                  const Icon = config.icon;
                  return (
                    <button
                      key={config.platform}
                      type="button"
                      disabled={!isProfileReady || isConnecting}
                      onClick={() => setSelectedPlatform(config.platform)}
                      className="flex flex-col items-center gap-3 p-6 rounded-lg border hover:bg-muted/50 cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <div className="h-12 w-12 rounded-full flex items-center justify-center bg-muted">
                        <Icon className="h-6 w-6 text-muted-foreground" />
                      </div>
                      <span className="text-sm font-medium">
                        {getPlatformLabel(config.platform)}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Connected channels — full-width rows */}
              {connectedChannels.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                    Connected
                  </p>
                  <div className="space-y-2">
                    {connectedChannels.map((channel) => {
                      const config = PLATFORM_CONFIGS.find(
                        (c) => c.platform === channel.platform,
                      );
                      if (!config) return null;
                      const Icon = config.icon;
                      return (
                        <div
                          key={channel.id}
                          className="flex items-center gap-3 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3"
                        >
                          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10">
                            <Icon className="h-4 w-4 text-primary" />
                          </div>
                          <div className="flex flex-col min-w-0 flex-1">
                            <span className="text-sm font-medium truncate">
                              {channel.handle ??
                                channel.displayName ??
                                getPlatformLabel(channel.platform)}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {getPlatformLabel(channel.platform)}
                            </span>
                          </div>
                          <button
                            type="button"
                            disabled={isDisconnecting}
                            onClick={() => onDisconnect(channel.id)}
                            className="shrink-0 rounded-md p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            aria-label={`Remove ${getPlatformLabel(channel.platform)} account`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {connectedChannels.length === 0 && (
                <div className="rounded-lg border p-4">
                  <p className="text-sm text-muted-foreground">
                    Looking for UGC opportunities? You can continue without
                    connecting any social accounts to discover UGC campaigns.
                  </p>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Creator Basics
// ---------------------------------------------------------------------------

function StepBasics({
  firstName,
  lastName,
  city,
  state,
  country,
  onFirstNameChange,
  onLastNameChange,
  onCityChange,
  onStateChange,
  onCountryChange,
}: {
  firstName: string;
  lastName: string;
  city: string;
  state: string;
  country: string;
  onFirstNameChange: (v: string) => void;
  onLastNameChange: (v: string) => void;
  onCityChange: (v: string) => void;
  onStateChange: (v: string) => void;
  onCountryChange: (v: string) => void;
}) {
  const [countryOpen, setCountryOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);

  const regions = useMemo(
    () => (country ? getRegions(country) : []),
    [country],
  );

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground text-center">
        Tell us a bit about yourself so we can match you with the right
        opportunities.
      </p>

      <Card>
        <CardContent className="p-4 space-y-4">
          <div className="space-y-2">
            <Label htmlFor="firstName">First Name *</Label>
            <Input
              id="firstName"
              placeholder="First name"
              value={firstName}
              onChange={(e) => onFirstNameChange(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">Last Name *</Label>
            <Input
              id="lastName"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => onLastNameChange(e.target.value)}
            />
          </div>

          {/* Country — searchable combobox */}
          <div className="space-y-2">
            <Label>Country *</Label>
            <Popover open={countryOpen} onOpenChange={setCountryOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  role="combobox"
                  aria-expanded={countryOpen}
                  className="w-full justify-between font-normal"
                >
                  <span className="truncate">
                    {country || "Select country..."}
                  </span>
                  <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                <Command>
                  <CommandInput placeholder="Search country..." />
                  <CommandList>
                    <CommandEmpty>No country found.</CommandEmpty>
                    <CommandGroup>
                      {countries.map((c) => (
                        <CommandItem
                          key={c.shortCode}
                          value={c.name}
                          onSelect={(value) => {
                            onCountryChange(value);
                            if (value !== country) {
                              onStateChange("");
                            }
                            setCountryOpen(false);
                          }}
                        >
                          <Check
                            className={`mr-2 h-4 w-4 ${
                              country === c.name ? "opacity-100" : "opacity-0"
                            }`}
                          />
                          {c.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </div>

          {/* State/Province — combobox when regions available, text input fallback */}
          <div className="space-y-2">
            <Label>State / Province *</Label>
            {regions.length > 0 ? (
              <Popover open={stateOpen} onOpenChange={setStateOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={stateOpen}
                    className="w-full justify-between font-normal"
                  >
                    <span className="truncate">
                      {state || "Select state..."}
                    </span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
                  <Command>
                    <CommandInput placeholder="Search state..." />
                    <CommandList>
                      <CommandEmpty>No state found.</CommandEmpty>
                      <CommandGroup>
                        {regions.map((region) => (
                          <CommandItem
                            key={region.shortCode}
                            value={region.name}
                            onSelect={(value) => {
                              onStateChange(value);
                              setStateOpen(false);
                            }}
                          >
                            <Check
                              className={`mr-2 h-4 w-4 ${
                                state === region.name
                                  ? "opacity-100"
                                  : "opacity-0"
                              }`}
                            />
                            {region.name}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            ) : (
              <Input
                id="state"
                placeholder={
                  country ? "Enter state..." : "Select country first"
                }
                disabled={!country}
                value={state}
                onChange={(e) => onStateChange(e.target.value)}
              />
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="city">City *</Label>
            <Input
              id="city"
              placeholder="Los Angeles"
              value={city}
              onChange={(e) => onCityChange(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 2: Campaign Interests
// ---------------------------------------------------------------------------

function StepCampaignInterests({
  campaignTypes,
  campaignOtherText,
  onCampaignTypesChange,
  onCampaignOtherTextChange,
}: {
  campaignTypes: string[];
  campaignOtherText: string;
  onCampaignTypesChange: (v: string[]) => void;
  onCampaignOtherTextChange: (v: string) => void;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground text-center">
        Pick the types of brand collaborations that excite you most.
      </p>
      <Card>
        <CardContent className="p-4">
          <ToggleGroup
            type="multiple"
            value={campaignTypes}
            onValueChange={onCampaignTypesChange}
            className="grid grid-cols-2 gap-3"
          >
            {CAMPAIGN_TYPE_OPTIONS.map((option) => (
              <ToggleGroupItem
                key={option}
                value={option}
                variant="outline"
                className="rounded-full px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
              >
                {option}
              </ToggleGroupItem>
            ))}
            <ToggleGroupItem
              value="Other"
              variant="outline"
              className="rounded-full px-4 py-2 data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
            >
              Other
            </ToggleGroupItem>
          </ToggleGroup>
          {campaignTypes.includes("Other") && (
            <Input
              className="mt-3"
              placeholder="Tell us more..."
              value={campaignOtherText}
              onChange={(e) => onCampaignOtherTextChange(e.target.value)}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Content Categories
// ---------------------------------------------------------------------------

function StepContentCategories({
  contentCategories,
  onContentCategoriesChange,
}: {
  contentCategories: string[];
  onContentCategoriesChange: (v: string[]) => void;
}) {
  const atMax = contentCategories.length >= MAX_CONTENT_CATEGORIES;

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground text-center">
        Choose up to {MAX_CONTENT_CATEGORIES} categories that best describe your
        content.
      </p>
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-end mb-3">
            <span className="text-xs text-muted-foreground">
              {contentCategories.length}/{MAX_CONTENT_CATEGORIES}
            </span>
          </div>
          <ToggleGroup
            type="multiple"
            value={contentCategories}
            onValueChange={onContentCategoriesChange}
            className="grid grid-cols-2 gap-3"
          >
            {CONTENT_CATEGORY_OPTIONS.map((option) => {
              const isSelected = contentCategories.includes(option);
              return (
                <ToggleGroupItem
                  key={option}
                  value={option}
                  variant="outline"
                  disabled={atMax && !isSelected}
                  className="rounded-full px-3 py-2 text-sm data-[state=on]:bg-primary data-[state=on]:text-primary-foreground data-[state=on]:border-primary"
                >
                  {option}
                </ToggleGroupItem>
              );
            })}
          </ToggleGroup>
        </CardContent>
      </Card>
    </div>
  );
}
